import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bike, FileDown, PlayCircle, StopCircle } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BikeCards } from "@/components/fleet/BikeCards";
import { FaultsList } from "@/components/fleet/FaultsList";
import { RunsList } from "@/components/fleet/RunsList";
import { ChecklistEditor } from "@/components/fleet/ChecklistEditor";
import { RecurringAlert } from "@/components/fleet/RecurringAlert";
import { FleetReportDialog } from "@/components/fleet/FleetReportDialog";
import { BudgetWarning } from "@/components/trustee/BudgetWarning";

export default function Fleet() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bikes, setBikes] = useState([]);
  const [el, setEl] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [report, setReport] = useState(false);
  const canManage = (user.permissions?.fleet || 0) >= 3;
  const canRun = (user.permissions?.fleet || 0) >= 1 && user.role !== "trustee";
  const seeFaults = (user.permissions?.fleet || 0) >= 2;

  useEffect(() => {
    api.get("/bikes").then((r) => setBikes(r.data));
    if (canRun) api.get("/fleet/eligibility").then((r) => setEl(r.data));
    if (["admin", "fleet_manager", "trustee"].includes(user.role)) {
      api.get("/fleet/recurring").then((r) => setRecurring(r.data)).catch(() => {});
      api.get("/fleet/compliance").then((r) => setAlerts(r.data.flatMap((b) => b.items.filter((i) => ["overdue", "due_soon"].includes(i.state)).map((i) => ({ ...i, bike_id: b.bike_id, bike_name: b.bike_name })))));
    }
  }, [canRun, user.role]);

  const pdf = () => setReport(true);
  const upd = (b) => { setBikes((x) => x.map((y) => (y.id === b.id ? { ...y, ...b } : y))); window.dispatchEvent(new Event("fleet-changed")); };

  return (
    <div className="space-y-8">
      <div className="on-photo relative overflow-hidden rounded-3xl border">
        <img src="/brand/latest-news.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#111317]/75" />
        <div className="relative flex flex-col gap-5 p-6 sm:p-10 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Blood bike runs</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Fleet & bike checks</h1>
            <p className="mt-2 max-w-lg text-sm text-zinc-300">Pre-ride check before you leave, post-ride check when you return. Report any faults, damage or missing items.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {el?.open_run ? (
              <button onClick={() => nav(`/bike-check/${el.open_run.id}/post`)} data-testid="start-post-check-btn" className="inline-flex items-center gap-2 rounded-full bg-destructive px-6 py-3 font-bold text-white hover:bg-red-700"><StopCircle className="h-5 w-5" /> Post-ride check · {el.open_run.bike_name}</button>
            ) : canRun && el?.eligible ? (
              <button onClick={() => nav("/bike-check")} data-testid="start-pre-check-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground"><PlayCircle className="h-5 w-5" /> Start pre-ride check</button>
            ) : null}
            {["admin", "fleet_manager", "trustee"].includes(user.role) && (
              <button onClick={pdf} data-testid="fleet-pdf-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/30 px-5 py-3 text-sm font-semibold text-white hover:bg-black/50"><FileDown className="h-4 w-4" /> Fleet PDF</button>
            )}
          </div>
        </div>
      </div>
      {canRun && el && !el.eligible && <p data-testid="not-eligible-msg" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">{el.reason}</p>}
      <RecurringAlert items={recurring} testid="fleet-recurring-alert" />
      {["admin", "fleet_manager", "trustee"].includes(user.role) && <BudgetWarning testid="fleet-budget-warning" linkTo="/trustees#budgets" />}
      {report && <FleetReportDialog onClose={() => setReport(false)} />}
      {alerts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="compliance-alerts">
          {alerts.map((a) => (
            <Link key={a.bike_id + a.item} to={`/fleet/bikes/${a.bike_id}`} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${a.state === "overdue" ? "border-red-500/50 bg-red-500/10 text-red-300" : "border-yellow-400/50 text-yellow-300"}`}>
              {a.bike_name}: {a.item} {a.state === "overdue" ? "overdue" : "due soon"}
            </Link>
          ))}
        </div>
      )}

      <Tabs defaultValue="bikes">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="bikes" data-testid="tab-bikes"><Bike className="mr-1.5 h-4 w-4" /> Bikes</TabsTrigger>
          {seeFaults && <TabsTrigger value="faults" data-testid="tab-faults">Faults</TabsTrigger>}
          <TabsTrigger value="runs" data-testid="tab-runs">{user.role === "rider" ? "My runs" : "Runs"}</TabsTrigger>
          {canManage && <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>}
        </TabsList>
        <TabsContent value="bikes" className="pt-4"><BikeCards bikes={bikes} canManage={canManage} onChange={upd} onAdd={(b) => setBikes((x) => [...x, { ...b, open_faults: 0 }])} /></TabsContent>
        {seeFaults && <TabsContent value="faults" className="pt-4"><FaultsList canManage={canManage} /></TabsContent>}
        <TabsContent value="runs" className="pt-4"><RunsList /></TabsContent>
        {canManage && <TabsContent value="checklist" className="pt-4"><ChecklistEditor /></TabsContent>}
      </Tabs>
    </div>
  );
}
